import classNames from 'clsx';
import React, { type ReactElement, memo } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType, INTEGRATION_STORAGES } from 'reactApp/modules/storage/storage.types';
import { WorkspaceSuperAppService } from 'reactApp/services';
import { noopVoid } from 'reactApp/utils/helpers';

import styles from './Link.css';
import { getRouterLinkUrl } from './Link.helpers';
import type { IProps } from './Link.types';

export const Link = memo(
    ({
        children,
        id,
        href,
        storage,
        handleGoTo = noopVoid,
        handleClick = noopVoid,
        noLink = false,
        search = '',
        className = '',
    }: IProps): ReactElement => {
        const currentStorage = useSelector(getCurrentStorage);
        const isArchive = storage === EStorageType.archive;
        const isRouterLink = storage && currentStorage && !isArchive;
        const isRouterLinkPublic = !isArchive && (currentStorage === EStorageType.public || currentStorage === EStorageType.stock);
        const currentIntegrationStorage = INTEGRATION_STORAGES[currentStorage];
        const routerLinkUrl = id && storage ? getRouterLinkUrl(id, currentIntegrationStorage || storage, href, search) : undefined;

        const handleMouseDown = (e: React.MouseEvent<HTMLAnchorElement>) => {
            const isWorkspaceSuperAppMode = WorkspaceSuperAppService.isEnabled;
            if (!isWorkspaceSuperAppMode) {
                return;
            }

            if (e.button === 0) {
                e.currentTarget.href = routerLinkUrl;
            } else if (e.button === 2) {
                e.currentTarget.href = `${WorkspaceSuperAppService.superAppOrigin.origin}/disk${routerLinkUrl}`;
            }
        };

        return (
            <>
                {!noLink && (isRouterLink || isRouterLinkPublic) && routerLinkUrl ? (
                    <RouterLink
                        draggable="false"
                        to={routerLinkUrl}
                        className={classNames(styles.root, className)}
                        onClick={handleClick}
                        onMouseDown={handleMouseDown}
                    >
                        {children}
                    </RouterLink>
                ) : (
                    <div onClick={handleGoTo} className={classNames(styles.root, className)}>
                        {children}
                    </div>
                )}
            </>
        );
    }
);

Link.displayName = 'Link';
